import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Send, Phone, Mail, MapPin, Loader2, CheckCircle, Sparkles } from "lucide-react";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Use relative URL - works on any server
const API = "/api";

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "0px" });
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        company: "",
        phone: "",
        email: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "WhatsApp",
      value: "+92 318 519 4055",
      href: "https://wa.me/923185194055",
      color: "green",
    },
    {
      icon: Mail,
      label: "Email",
      value: "mightyroutes@gmail.com",
      href: "mailto:mightyroutes@gmail.com",
      color: "blue",
    },
    {
      icon: MapPin,
      label: "Office Address",
      value: "Office No. 1, 1st Floor, Ahmed Plaza, Range Rd, Afshan Colony, Rawalpindi, 46000, Pakistan",
      href: "https://www.google.com/maps/place/NUVEXA+LLC/@33.5979503,73.0204955,17z",
      color: "purple",
    },
  ];

  const colorClasses = {
    green: "bg-green-500/10 text-green-400 hover:bg-green-500/20",
    blue: "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20",
    purple: "bg-purple-500/10 text-purple-400 hover:bg-purple-500/20",
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      <Toaster position="top-right" richColors />
      
      {/* Background decorations */}
      <motion.div
        className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1 }}
      />
      
      <motion.div
        className="absolute top-40 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          x: [0, 30, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          y: [0, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span 
            className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-blue-400 font-medium mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Sparkles size={16} className="animate-pulse" />
            Get In Touch
          </motion.span>
          <motion.h2 
            className="text-3xl md:text-5xl font-bold tracking-tight mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
          >
            Let's Build Something <span className="text-gradient">Amazing</span>
          </motion.h2>
          <motion.p 
            className="text-lg text-slate-400 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
          >
            Ready to transform your call center operations? Get in touch and let's 
            discuss how we can help.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left - Contact info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-semibold text-white mb-6">
              Contact Information
            </h3>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Have a project in mind? Reach out to us through any of these channels 
              and we'll respond within 24 hours.
            </p>

            <div className="space-y-6 mb-10">
              {contactInfo.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith("http") ? "_blank" : undefined}
                  rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="flex items-center gap-4 group"
                  data-testid={`contact-info-${index}`}
                >
                  <motion.div 
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${colorClasses[item.color]}`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <item.icon size={22} />
                  </motion.div>
                  <div>
                    <div className="text-sm text-slate-400">{item.label}</div>
                    <span className="text-white group-hover:text-blue-400 transition-colors font-medium">
                      {item.value}
                    </span>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Quick response badge */}
            <motion.div 
              className="glass-card p-6 inline-block mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center gap-3">
                <motion.div 
                  className="w-3 h-3 bg-green-500 rounded-full"
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [1, 0.7, 1],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                <span className="text-slate-300">
                  Average response time: <span className="text-white font-medium">2 hours</span>
                </span>
              </div>
            </motion.div>

            {/* Google Maps Embed */}
            <motion.div 
              className="glass-card p-2 overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.02 }}
              data-testid="google-map"
            >
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d207.70493151712193!2d73.02318191620705!3d33.59806723069495!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2s!4v1771764762409!5m2!1sen!2s" 
                width="100%" 
                height="200" 
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Mighty Routes Office Location"
              />
            </motion.div>
          </motion.div>

          {/* Right - Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.form 
              onSubmit={handleSubmit} 
              className="glass-card p-8 relative overflow-hidden"
              whileHover={{ boxShadow: "0 25px 50px rgba(59, 130, 246, 0.1)" }}
              data-testid="contact-form"
            >
              {/* Animated shine effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent pointer-events-none"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 4, repeat: Infinity, repeatDelay: 3 }}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 relative z-10">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Your Name *
                  </label>
                  <motion.input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="form-input w-full bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 transition-all"
                    placeholder="John Smith"
                    animate={{ 
                      borderColor: focusedField === 'name' ? '#3b82f6' : '#1e293b',
                      boxShadow: focusedField === 'name' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    }}
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Company
                  </label>
                  <motion.input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('company')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input w-full bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 transition-all"
                    placeholder="Your Company"
                    animate={{ 
                      borderColor: focusedField === 'company' ? '#3b82f6' : '#1e293b',
                      boxShadow: focusedField === 'company' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    }}
                    data-testid="input-company"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6 relative z-10">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number
                  </label>
                  <motion.input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    className="form-input w-full bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 transition-all"
                    placeholder="+1 234 567 890"
                    animate={{ 
                      borderColor: focusedField === 'phone' ? '#3b82f6' : '#1e293b',
                      boxShadow: focusedField === 'phone' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    }}
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email Address *
                  </label>
                  <motion.input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="form-input w-full bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 transition-all"
                    placeholder="john@company.com"
                    animate={{ 
                      borderColor: focusedField === 'email' ? '#3b82f6' : '#1e293b',
                      boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                    }}
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="mb-6 relative z-10">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Your Message *
                </label>
                <motion.textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                  rows={5}
                  className="form-input w-full bg-slate-950/50 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder:text-slate-600 transition-all resize-none"
                  placeholder="Tell us about your project..."
                  animate={{ 
                    borderColor: focusedField === 'message' ? '#3b82f6' : '#1e293b',
                    boxShadow: focusedField === 'message' ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
                  }}
                  data-testid="input-message"
                />
              </div>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white rounded-full px-8 py-4 font-medium transition-all duration-300 btn-glow flex items-center justify-center gap-2 relative overflow-hidden group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="submit-btn"
              >
                {/* Button shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.5 }}
                />
                
                <AnimatePresence mode="wait">
                  {isSubmitting ? (
                    <motion.div
                      key="loading"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2"
                    >
                      <Loader2 className="animate-spin" size={18} />
                      Sending...
                    </motion.div>
                  ) : (
                    <motion.div
                      key="submit"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2"
                    >
                      Send Message
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <Send size={18} />
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
